import { useState } from 'react';

import { Group, Slider, Stack, ThemeIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import Cropper, { Area, CropperProps, Point } from 'react-easy-crop';
import { IconRotateLeft, IconRotateRight, IconZoomMinus, IconZoomPlus } from 'ui-icons';

import { ActionIcon, ButtonMantine } from '../../../Buttons';
import { Card } from '../../../DataDisplay';
import { Box } from '../../../Miscellaneous/Box';
import { Divider } from '../../../Miscellaneous/Divider/Divider';
import { Modal, ModalProps, Tooltip } from '../../../Overlays';
import { getCroppedImg } from '../../../Utils/getCroppedImage';

interface ImageCropperProps {
  /**
   * The image to be cropped
   */
  imageUrl: string;
  /**
   * Fired when the user finishes cropping the image.
   */
  onCropped: (croppedImage: File) => void;
  onClose: () => void;
  /**
   * The dimensions of the cropped image
   * @default [600, 300]
   */
  dimensions?: [number, number];
  /**
   * The name of the newly cropped image file
   */
  croppedImageFileName?: string;
  /**
   * Whether the crop area should be circular
   */
  isCircularCrop?: boolean;
}

interface ImageCropperModalProps extends ModalProps, Omit<ImageCropperProps, 'imageUrl'> {
  imageUrl?: string;
}

const ImageCropper = ({
  imageUrl,
  onCropped,
  onClose,
  dimensions = [600, 300],
  croppedImageFileName,
  isCircularCrop = false,
}: ImageCropperProps) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [rotation, setRotation] = useState<number>(0);

  // Fired when the user finishes dragging the crop area
  // Used to set the local state of the crop dimensions
  const onCropComplete: CropperProps['onCropComplete'] = async (
    _croppedArea,
    croppedAreaPixels
  ) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Fired when the user clicks the "Finish Cropping" button
  const onCropConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    try {
      const croppedImage = await getCroppedImg({
        imageUrl,
        pixelCrop: croppedAreaPixels,
        rotation,
        croppedImageFileName,
      });

      if (!croppedImage) {
        return;
      }
      onCropped(croppedImage);
    } catch (error) {
      notifications.show({
        message: `Couldn't crop image: ${error}`,
        variant: 'error',
        autoClose: 5000,
      });
    }
  };
  if (!imageUrl) return null;
  return (
    <Stack gap="lg" pos="relative">
      <Box pos="relative" h={317}>
        <Cropper
          objectFit="cover"
          minZoom={0.1}
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={dimensions[0] / dimensions[1]}
          onCropComplete={onCropComplete}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          rotation={rotation}
          restrictPosition={false}
          onMediaLoaded={() => setZoom(1)}
          onRotationChange={setRotation}
          cropShape={isCircularCrop ? 'round' : 'rect'}
          style={{
            containerStyle: { borderRadius: '8px' },
          }}
        />
      </Box>

      {/* Zoom label and slider */}
      <Card py={0} px="sm" bg="gray.0" bd="1px solid gray.1" radius="sm" h={40}>
        <Group gap="sm" h="100%">
          <ThemeIcon variant="transparent" color="gray.4" size={16} bd="none">
            <IconZoomMinus />
          </ThemeIcon>

          <Slider
            label={null}
            value={zoom}
            onChange={setZoom}
            min={0.1}
            max={3}
            step={0.01}
            style={{ flex: 1 }}
          />
          <ThemeIcon variant="transparent" color="gray.4" size={16} bd="none">
            <IconZoomPlus />
          </ThemeIcon>

          <Group gap="sm" h="100%">
            <Divider orientation="vertical" />

            <Tooltip label="Rotate Counter-Clockwise 90°" position="top">
              <ActionIcon
                size={16}
                bd="none"
                variant="transparent"
                c="gray.4"
                p={0}
                style={{ overflow: 'visible' }}
                onClick={() => {
                  setCrop({ x: 0, y: 0 });
                  setRotation((rotation) => (rotation + 270) % 360);
                }}
              >
                <IconRotateLeft />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" />
            <Tooltip label="Rotate Clockwise 90°" position="top">
              <ActionIcon
                size={16}
                bd="none"
                variant="transparent"
                c="gray.4"
                p={0}
                style={{ overflow: 'visible' }}
                onClick={() => {
                  setCrop({ x: 0, y: 0 });
                  setRotation((rotation) => (rotation + 90) % 360);
                }}
              >
                <IconRotateRight />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {/* Footer */}
      <Group mt="xl" justify="flex-start" gap="md" style={{ flexDirection: 'row-reverse' }}>
        <ButtonMantine size="md" onClick={onCropConfirm}>
          Finish Cropping
        </ButtonMantine>
        <ButtonMantine size="md" variant="subtle" onClick={onClose}>
          Cancel
        </ButtonMantine>
      </Group>
    </Stack>
  );
};

const ImageCropperModal = ({ onClose, opened, imageUrl, ...props }: ImageCropperModalProps) => {
  return (
    <Modal title="Crop Image" onClose={onClose} opened={opened && !!imageUrl}>
      <ImageCropper onClose={onClose} imageUrl={imageUrl ?? ''} {...props} />
    </Modal>
  );
};

export { ImageCropper, ImageCropperModal };
